import type { GetTripDetailsResponse } from '../types/api/v1/obt/trip/trip_details';
import type { AirPnrTravelerInfoSeatInfo, Pnr, Ticket, TicketFlightCoupon } from '../types/api/v1/obt/pnr/pnr';
import type { AirSearchResponse, RateOptionTicketType, FareType } from '../types/api/v1/obt/air/air_search_response';
import type { ITripPassengersFareBreakup, ITripPnr } from '../types/trip';
import { IsRefundableEnum } from '../types/trip';
import type { ILegAirports, ITicketAirports, ILegBaseFare } from '../types/flight';
import { TicketTypeEnum } from '../types/flight';
import { MoneyUtil } from '../utils/Money';
import { getMerchantFeeFromPnr } from '../utils/trips';
import TripDetailsResponseManager from './TripDetailsResponseManager';
import type { CTC } from '../types/api/v1/obt/pnr/payment';
import type { Money } from '../types/api/v1/common/money';

export default class AirPnrManager extends TripDetailsResponseManager {
  constructor(readonly response: GetTripDetailsResponse) {
    super(response);
  }

  public GetPassengerAirPnrTickets(sourcePnrId: string, passengerIndex: number): Ticket[] {
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    return pnrDetails?.air?.travelerInfo[passengerIndex].tickets ?? [];
  }

  static GetTicketType(pnr: ITripPnr): RateOptionTicketType {
    return pnr.air?.air?.itineraries[0].rateOptions[0].ticketType ?? TicketTypeEnum.SINGLE;
  }

  static GetFareType(pnr: ITripPnr): FareType | undefined {
    const legIndex = pnr.legInfoArrayIndex ?? 0;

    return pnr.air?.air?.itineraries[0].rateOptions[0].paxInfo[0].fareComponents[0].legInfo?.[legIndex]?.fareType;
  }

  static GetPrimaryTravelerTicketsCount(pnr: ITripPnr): number {
    return pnr.air?.travelerInfo[0]?.tickets?.length ?? 0;
  }

  static GetTotalCO2Emission(pnr: ITripPnr, legIndex: number, unit: 'tonnes' | 'kg' = 'kg'): number | undefined {
    const totalEmissionTonnes =
      pnr.air?.air?.itineraries[0]?.rateOptions[0]?.paxInfo[0]?.fareComponents[0]?.legInfo[legIndex]?.flightInfo.reduce(
        (sum, info) => {
          const flightCO2EmissionValue = info.co2EmissionDetail?.emissionValue ?? 0;

          return sum + flightCO2EmissionValue;
        },
        0,
      ) ?? 0;

    if (unit === 'kg') {
      return Math.round(totalEmissionTonnes * 1000);
    }

    return Number(totalEmissionTonnes.toFixed(3));
  }

  static GetIsCo2EmissionValueApprox(pnr: ITripPnr, legIndex: number): boolean | undefined {
    const isCo2EmissionValueApprox =
      pnr.air?.air?.itineraries[0]?.rateOptions[0]?.paxInfo[0]?.fareComponents[0]?.legInfo[legIndex]?.flightInfo[0]
        .co2EmissionDetail?.isApproximate;
    return isCo2EmissionValueApprox;
  }

  public IsPnrRefundable(sourcePnrId: string, passengerIndex: number): IsRefundableEnum {
    const pnrTickets = this.GetPassengerAirPnrTickets(sourcePnrId, passengerIndex);
    if (pnrTickets) {
      if (pnrTickets.length) {
        // if any of the ticket's isInfoAvailable is false : contact us (i.e return IsRefundableEnum.UNRECOGNIZED)
        // if both refundable/non-refundable then show cumulative penalty else, contact support(i.e return IsRefundableEnum.UNRECOGNIZED)
        const allInfoAvailable = pnrTickets.every(
          (ticket) => ticket.cancellationPolicy?.refundPolicy?.isInfoAvailable === true,
        );
        if (!allInfoAvailable) {
          return IsRefundableEnum.UNRECOGNIZED;
        }
        const allRefundable = pnrTickets.every(
          (ticket) => ticket.cancellationPolicy?.refundPolicy?.isRefundable === true,
        );
        if (allRefundable) {
          return IsRefundableEnum.TRUE;
        }
        const allNonRefundable = pnrTickets.every(
          (ticket) => ticket.cancellationPolicy?.refundPolicy?.isRefundable === false,
        );
        if (allNonRefundable) {
          return IsRefundableEnum.FALSE;
        }
        return IsRefundableEnum.UNRECOGNIZED;
      }
    }
    return IsRefundableEnum.UNRECOGNIZED;
  }

  public IsPnrExchangeable(sourcePnrId: string, passengerIndex: number): boolean {
    const pnrTickets = this.GetPassengerAirPnrTickets(sourcePnrId, passengerIndex);
    const isRefundable = this.IsPnrRefundable(sourcePnrId, passengerIndex);
    if (isRefundable === IsRefundableEnum.FALSE) {
      if (pnrTickets) {
        if (pnrTickets.length) {
          const allExchangeable = pnrTickets.every(
            (ticket) => ticket.cancellationPolicy?.exchangePolicy?.isExchangeable === true,
          );
          if (allExchangeable) {
            return true;
          }
        }
      }
    }
    return false;
  }

  public GetPassengerTicketsBaseFare(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const pnrTickets = this.GetPassengerAirPnrTickets(sourcePnrId, passengerIndex);
    const baseFare = MoneyUtil.parse(pnrTickets[0]?.amount?.base);
    const currency = baseFare.getCurrency();
    const originalCurrency = baseFare.getOriginalCurrency();
    return pnrTickets.reduce(
      (totalFare, currentFare) => MoneyUtil.parse(currentFare?.amount?.base).add(totalFare),
      MoneyUtil.zeroMoneyWithOriginal(currency, originalCurrency),
    );
  }

  protected GetZeroMoney(sourcePnrId: string): MoneyUtil {
    const baseFare = this.GetPassengerTicketsBaseFare(sourcePnrId, 0);
    return MoneyUtil.zeroMoneyWithOriginal(baseFare.getCurrency(), baseFare.getOriginalCurrency());
  }

  public GetPassengerTicketsTax(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const pnrTickets = this.GetPassengerAirPnrTickets(sourcePnrId, passengerIndex);
    const zeroMoney = this.GetZeroMoney(sourcePnrId);
    return pnrTickets.reduce(
      (totalFare, currentFare) => MoneyUtil.parse(currentFare?.amount?.tax).add(totalFare),
      zeroMoney,
    );
  }

  public GetPassengerSeatPrice(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    const zeroMoney = this.GetZeroMoney(sourcePnrId);
    const seatInfo: AirPnrTravelerInfoSeatInfo[] = pnrDetails?.air?.travelerInfo[passengerIndex]?.seatInfo ?? [];
    const allSeatPrice = AirPnrManager.GetAllSeatPrice(seatInfo);
    return allSeatPrice.reduce(
      (totalFare, currentFare) => (currentFare.isZero() ? zeroMoney.add(totalFare) : currentFare.add(totalFare)),
      zeroMoney,
    );
  }

  public GetPassengerTotalRefundPenalty(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const pnrTickets = this.GetPassengerAirPnrTickets(sourcePnrId, passengerIndex);
    const zeroMoney = this.GetZeroMoney(sourcePnrId);
    const isExchangeable = this.IsPnrExchangeable(sourcePnrId, passengerIndex);
    if (isExchangeable) {
      return zeroMoney;
    }
    return pnrTickets.reduce((totalPenalty, currentPenalty) => {
      const refundPenalty = MoneyUtil.parse(currentPenalty.cancellationPolicy?.refundPolicy?.refundPenalty);
      return refundPenalty.isZero() ? zeroMoney.add(totalPenalty) : refundPenalty.add(totalPenalty);
    }, zeroMoney);
  }

  public GetPassengerTotalExchangePenalty(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const pnrTickets = this.GetPassengerAirPnrTickets(sourcePnrId, passengerIndex);
    const zeroMoney = this.GetZeroMoney(sourcePnrId);
    return pnrTickets.reduce((totalPenalty, currentPenalty) => {
      const exchangePenalty = MoneyUtil.parse(currentPenalty.cancellationPolicy?.exchangePolicy?.exchangePenalty);
      return exchangePenalty.isZero() ? zeroMoney.add(totalPenalty) : exchangePenalty.add(totalPenalty);
    }, zeroMoney);
  }

  public GetPassengerAirlineCredit(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const totalFare = this.GetPassengerTotalFlightsPrice(sourcePnrId, passengerIndex);
    const penalty = this.GetPassengerTotalExchangePenalty(sourcePnrId, passengerIndex);
    let total;
    total = totalFare;
    if (penalty) {
      total = total.subtract(penalty);
    }
    return total;
  }

  private GetPassengerTotalFlightsPrice(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const baseFare = this.GetPassengerTicketsBaseFare(sourcePnrId, passengerIndex);
    const tax = this.GetPassengerTicketsTax(sourcePnrId, passengerIndex);
    const seatPrice = this.GetPassengerSeatPrice(sourcePnrId, passengerIndex);
    let total;
    total = baseFare;
    if (tax) {
      total = total.add(tax);
    }
    if (seatPrice) {
      total = total.add(seatPrice);
    }
    return total;
  }

  public GetPassengerTotalRefund(sourcePnrId: string, passengerIndex: number): MoneyUtil {
    const totalFare = this.GetPassengerTotalFlightsPrice(sourcePnrId, passengerIndex);
    const refundPenalty = this.GetPassengerTotalRefundPenalty(sourcePnrId, passengerIndex);
    const isExchangeable = this.IsPnrExchangeable(sourcePnrId, passengerIndex);

    const pnr = this.GetPnrDetails(sourcePnrId);
    const merchantFee = pnr ? getMerchantFeeFromPnr(pnr) : null;

    if (isExchangeable) {
      return MoneyUtil.zeroMoneyWithOriginal(totalFare.getCurrency(), totalFare.getOriginalCurrency());
    }

    const total = merchantFee ? totalFare.subtract(merchantFee) : totalFare;
    return refundPenalty ? total.subtract(refundPenalty) : total;
  }

  public getPassengersFareBreakup(sourcePnrId: string): ITripPassengersFareBreakup[] {
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    const passengers =
      pnrDetails?.air?.travelerInfo.map((passenger, paxIndex) => ({
        name: passenger.traveler?.user?.name,
        baseFare: this.GetPassengerTicketsBaseFare(sourcePnrId, paxIndex),
        tax: this.GetPassengerTicketsTax(sourcePnrId, paxIndex),
        seatSelectionPrice: this.GetPassengerSeatPrice(sourcePnrId, paxIndex),
        totalFare: this.GetPassengerTotalFlightsPrice(sourcePnrId, paxIndex),
        tickets: passenger.tickets.map(({ amount }) => ({
          base: MoneyUtil.parse(amount?.base),
          tax: MoneyUtil.parse(amount?.tax),
        })),
        penalty: this.GetPassengerTotalRefundPenalty(sourcePnrId, paxIndex),
        flights: this.GetFlightsPerLeg(sourcePnrId),
      })) ?? [];

    return passengers;
  }

  public GetFlightsPerLeg(sourcePnrId: string): Record<number, ILegAirports[]> {
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    const flights = pnrDetails?.air?.air?.data.flights;
    const legs = pnrDetails?.air?.air?.data.legs;

    const flightsPerLeg: Record<number, ILegAirports[]> = [];

    legs?.forEach((leg, legIndex) => {
      flightsPerLeg[legIndex] = [];
      flights?.forEach((flight, flightIndex) => {
        if (leg.flightIndices.includes(flightIndex)) {
          flightsPerLeg[legIndex].push({
            departureAirportCode: flight.origin.airport,
            arrivalAirportCode: flight.destination.airport,
          });
        }
      });
    });

    return flightsPerLeg;
  }

  public GetTotalRefund(sourcePnrId: string): MoneyUtil {
    let totalRefund = this.GetZeroMoney(sourcePnrId);
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    pnrDetails?.air?.travelerInfo.forEach((_passengerInfo, paxIndex) => {
      totalRefund = totalRefund.add(this.GetPassengerTotalRefund(sourcePnrId, paxIndex));
    });
    return totalRefund;
  }

  public GetTotalAirlineCredit(sourcePnrId: string): MoneyUtil {
    let airlineCredit = this.GetZeroMoney(sourcePnrId);
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    pnrDetails?.air?.travelerInfo.forEach((_passengerInfo, paxIndex) => {
      airlineCredit = airlineCredit.add(this.GetPassengerAirlineCredit(sourcePnrId, paxIndex));
    });
    return airlineCredit;
  }

  public GetTotalFlightPrice(sourcePnrId: string): MoneyUtil {
    let totalFlightsPrice = this.GetZeroMoney(sourcePnrId);
    const pnrDetails = this.GetPnrDetails(sourcePnrId);
    pnrDetails?.air?.travelerInfo.forEach((_passengerInfo, paxIndex) => {
      totalFlightsPrice = totalFlightsPrice.add(this.GetPassengerTotalFlightsPrice(sourcePnrId, paxIndex));
    });
    return totalFlightsPrice;
  }

  public static IsSameFlightCoupon(
    flightCoupons1: TicketFlightCoupon[],
    flightCoupons2: TicketFlightCoupon[],
  ): boolean {
    if (flightCoupons1.length === flightCoupons2.length) {
      // eslint-disable-next-line no-unreachable-loop
      for (let couponIndex = 0; couponIndex < flightCoupons1.length; couponIndex += 1) {
        const flightCoupons2WithSameIndexes = flightCoupons2.findIndex(
          (coupon) =>
            coupon.flightIndex === flightCoupons1[couponIndex].flightIndex &&
            coupon.legIndex === flightCoupons1[couponIndex].legIndex,
        );
        if (flightCoupons2WithSameIndexes === -1) {
          return false;
        }
        return true;
      }
    }
    return false;
  }

  private static getTicketAmount(ticket: Ticket): {
    base: MoneyUtil;
    tax: MoneyUtil;
  } {
    const ticketCtc = ticket.ticketCtc?.cost ?? [];
    if (ticketCtc.length > 0) {
      const baseFare = MoneyUtil.parse(ticketCtc[0].amount?.base);
      const currency = baseFare.getCurrency();
      const originalCurrency = baseFare.getOriginalCurrency();
      let base = MoneyUtil.zeroMoneyWithOriginal(currency, originalCurrency);
      let tax = MoneyUtil.zeroMoneyWithOriginal(currency, originalCurrency);

      ticketCtc.forEach((currentCost) => {
        if (currentCost?.isRefunded) {
          base = base.subtract(MoneyUtil.parse(currentCost.amount?.base));
          tax = tax.subtract(MoneyUtil.parse(currentCost.amount?.tax));
        } else {
          base = base.add(MoneyUtil.parse(currentCost.amount?.base));
          tax = tax.add(MoneyUtil.parse(currentCost.amount?.tax));
        }
      });

      return { base, tax };
    }

    return {
      base: MoneyUtil.parse(ticket.amount?.base),
      tax: MoneyUtil.parse(ticket.amount?.tax),
    };
  }

  private static GetTicketLegs(tickets: Ticket[]): number[] {
    const ticketLegs: number[] = [];
    tickets?.forEach((ticket) => {
      ticket.flightCoupons.forEach((flightCoupon) => {
        if (!ticketLegs.includes(flightCoupon.legIndex)) {
          ticketLegs.push(flightCoupon.legIndex);
        }
      });
    });
    return ticketLegs;
  }

  private static GetTicketAirports(
    ticket: Ticket,
    airSearchResponse: AirSearchResponse | undefined,
  ): ITicketAirports[] {
    const ticketAirports: ITicketAirports[] = [];

    ticket.flightCoupons.forEach((flightCoupon) => {
      const ticketAirport = {
        departureAirport: airSearchResponse?.data?.flights[flightCoupon.flightIndex]?.origin?.airport ?? '',
        arrivalAirport: airSearchResponse?.data?.flights[flightCoupon.flightIndex]?.destination?.airport ?? '',
      };
      ticketAirports.push(ticketAirport);
    });
    return ticketAirports;
  }

  private static GetAirportsForLeg(
    airSearchResponse: AirSearchResponse | undefined,
    legIndex: number,
  ): ITicketAirports[] {
    const ticketAirports: ITicketAirports[] = [];
    const allFlightIndexes = airSearchResponse?.data?.legs[legIndex]?.flightIndices ?? [];

    allFlightIndexes.forEach((flightIndex) => {
      const ticketAirport = {
        departureAirport: airSearchResponse?.data?.flights[flightIndex]?.origin?.airport ?? '',
        arrivalAirport: airSearchResponse?.data?.flights[flightIndex]?.destination?.airport ?? '',
      };
      ticketAirports.push(ticketAirport);
    });

    return ticketAirports;
  }

  private static isTicketPriceAlreadyAdded(
    existingFlightCoupons: TicketFlightCoupon[][],
    flightCoupons: TicketFlightCoupon[],
  ): boolean {
    return existingFlightCoupons.some((existingCoupon) =>
      AirPnrManager.IsSameFlightCoupon(existingCoupon, flightCoupons),
    );
  }

  public static GetTicketPricesAndAirportsForMultiTicket(pnr: ITripPnr, paxIndex: number): ILegBaseFare[] {
    const travelerInfo = pnr.air?.travelerInfo ?? [];
    const travelerTicketInfo: ILegBaseFare[] = [];

    const allLegIndexes = pnr?.air?.legInfo.map((legDetail) => legDetail.legIndex) ?? [];
    const ticketLegs = AirPnrManager.GetTicketLegs(travelerInfo[paxIndex].tickets);

    const legsNotTicketed = allLegIndexes.filter((x) => !ticketLegs.includes(x));

    const uniquePassengerTickets: Ticket[] = [];
    if (travelerInfo[paxIndex].tickets) {
      const existingFlightCoupons: TicketFlightCoupon[][] = [];
      travelerInfo[paxIndex].tickets.forEach((ticket) => {
        if (!AirPnrManager.isTicketPriceAlreadyAdded(existingFlightCoupons, ticket.flightCoupons)) {
          existingFlightCoupons.push(ticket.flightCoupons);
          uniquePassengerTickets.push(ticket);
        }
      });
    }

    uniquePassengerTickets.forEach((ticket) => {
      const ticketAmount = AirPnrManager.getTicketAmount(ticket); // returns base and tax amount
      const ticketAirports = AirPnrManager.GetTicketAirports(ticket, pnr?.air?.air).map((airport) => ({
        departureAirportCode: airport.departureAirport,
        arrivalAirportCode: airport.arrivalAirport,
      }));
      travelerTicketInfo.push({
        flight: ticketAirports,
        baseFare: ticketAmount.base,
      });
    });

    // Return fare as null in case of non ticketed legs
    legsNotTicketed.forEach((currLegIndex) => {
      const flightCodes = AirPnrManager.GetAirportsForLeg(pnr?.air?.air, currLegIndex).map((airport) => ({
        departureAirportCode: airport.departureAirport,
        arrivalAirportCode: airport.arrivalAirport,
      }));
      travelerTicketInfo.push({
        baseFare: null,
        flight: flightCodes,
      });
    });

    return travelerTicketInfo;
  }

  public static GetExtraBaggageFee(pnr: ITripPnr, paxIndex: number): MoneyUtil {
    const travelerInfo = pnr.air?.travelerInfo ?? [];

    const baseFare = MoneyUtil.parse(pnr.air?.air?.itineraries[0].rateOptions[0]?.totalFare.base);

    const currency = baseFare.getCurrency();
    const originalCurrency = baseFare.getOriginalCurrency();
    const zeroMoney = MoneyUtil.zeroMoneyWithOriginal(currency, originalCurrency);

    // Summarize all the baggage fees
    const adder = (a: MoneyUtil, b: MoneyUtil | undefined) => a.add(b || zeroMoney);
    const reducer = (sum: MoneyUtil, fee: Money | undefined) => sum.add(fee ? MoneyUtil.parse(fee) : zeroMoney);

    const feesSumPerBaggageItem = travelerInfo[paxIndex]?.baggageInfo?.map((baggageInfoItem) =>
      baggageInfoItem?.baggage?.fee?.map((f) => f.fee).reduce(reducer, zeroMoney),
    );

    const totalBaggageFee = feesSumPerBaggageItem?.reduce(adder, zeroMoney);

    return totalBaggageFee;
  }

  public static GetGrandTotal(pnr: Pnr | null | undefined): MoneyUtil {
    const baseFare = MoneyUtil.parse(pnr?.air?.air?.itineraries[0].rateOptions[0].totalFare.base);
    const taxFare = MoneyUtil.parse(pnr?.air?.air?.itineraries[0].rateOptions[0].totalFare.tax);
    return pnr?.totalFare ? MoneyUtil.parse(pnr?.totalFare) : baseFare.add(taxFare);
  }

  public static GetCostToCustomer(
    grandTotal: MoneyUtil,
    costToCustomer: CTC | undefined,
  ): {
    hasMultipleFop: boolean;
    totalNonRefundAmount: MoneyUtil;
  } {
    const zeroMoney = MoneyUtil.zeroMoneyWithOriginal(grandTotal.getCurrency(), grandTotal.getOriginalCurrency());

    let totalNonRefundAmount = zeroMoney;

    if (!costToCustomer) {
      return {
        hasMultipleFop: false,
        totalNonRefundAmount: zeroMoney,
      };
    }

    const nonRefundedAmounts = costToCustomer.payments.filter((payment) => !payment.isRefunded);

    if (nonRefundedAmounts.length < 1) {
      return {
        hasMultipleFop: false,
        totalNonRefundAmount: zeroMoney,
      };
    }

    nonRefundedAmounts.forEach((nonRefundedAmount) => {
      const currentBase = nonRefundedAmount.amount?.base ? MoneyUtil.parse(nonRefundedAmount.amount?.base) : zeroMoney;
      const currentTax = nonRefundedAmount.amount?.tax ? MoneyUtil.parse(nonRefundedAmount.amount?.tax) : zeroMoney;
      const currentTotal = currentBase.add(currentTax);
      totalNonRefundAmount = currentTotal.add(totalNonRefundAmount);
    });

    if (totalNonRefundAmount.lessThan(grandTotal)) {
      return {
        hasMultipleFop: true,
        totalNonRefundAmount,
      };
    }

    return {
      hasMultipleFop: false,
      totalNonRefundAmount,
    };
  }
}
